import { atom, useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useInstanceId } from 'shared/src/hooks';
import { isAndroid } from 'shared/src/utils';
import type { OptionComponent } from 'shared/src/api';

type SelectedOptions = { [key: number]: OptionComponent };
type SelectedOptionsUpdater =
  | SelectedOptions
  | ((prev: SelectedOptions) => SelectedOptions);

const selectedOptionsAtom = atom<Record<string, SelectedOptions>>({});

export const useSelectedOptions = () => {
  const instanceId = useInstanceId();
  const derivedAtom = useMemo(
    () =>
      atom(
        get => get(selectedOptionsAtom)[instanceId] ?? {},
        (get, set, next: SelectedOptionsUpdater) => {
          const current = get(selectedOptionsAtom)[instanceId] ?? {};
          const value = typeof next === 'function' ? next(current) : next;
          set(selectedOptionsAtom, prev => ({ ...prev, [instanceId]: value }));
        },
      ),
    [instanceId],
  );
  const [value, setValue] = useAtom(derivedAtom);

  useEffect(() => {
    if (!isAndroid) {
      setValue({});
    }
  }, [setValue]);

  return [value, setValue] as const;
};
